/**
 * Accounts — `docs/API.md` §5.
 *
 * Fully implemented server-side: list (+ aggregates and stats), create, read,
 * patch, delete, restore, hierarchy and the account's own contacts sub-list all
 * live in `salescrmapi/src/modules/accounts`. `merge`, `bulk-assign`,
 * `duplicates`, and the `activities`/`opportunities`/`quotations`/`documents`
 * sub-resources answer `501 not_implemented` — none of those are exposed here.
 * An account's activities are read the same way a lead's are: `GET
 * /activities?account_id=<id>`, from the activities module, not a sub-resource
 * here — `accounts.routes.js` says so explicitly.
 */
import apiClient from './axiosClient.js';
import { withMockFallback } from './mockFallback.js';

/** @param {object} [params] any of listAccountsQuery's fields, e.g. `{ q, lifecycle_stage, with_stats }` */
export function listAccounts(params = {}) {
  return withMockFallback(
    'GET /accounts',
    async () => (await apiClient.get('/accounts', { params })).data,
    () => ({
      data: [],
      page: { limit: params.limit ?? 50, next_cursor: null, prev_cursor: null, has_more: false, total: 0 },
      aggregates: { by_stage: {}, by_health: {}, total_revenue: 0, by_segment: [], risk_exposure: { count: 0, revenue: 0 } },
    }),
  );
}

/** One account, with `stats` always included. @param {string} id */
export function getAccount(id) {
  return withMockFallback(
    `GET /accounts/${id}`,
    async () => (await apiClient.get(`/accounts/${id}`)).data,
    () => null,
  );
}

/**
 * Create an account. Carries an Idempotency-Key so a double-submit cannot
 * create the same company twice.
 * @param {object} payload at minimum `{ name }`
 */
export function createAccount(payload) {
  return withMockFallback(
    'POST /accounts',
    async () =>
      (await apiClient.post('/accounts', payload, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })).data,
    () => ({ ...payload, id: `AC-${Math.floor(Math.random() * 90000 + 10000)}`, row_version: 1 }),
  );
}

/**
 * Update an account under optimistic locking (§1.7).
 * @param {string} id
 * @param {object} patch
 * @param {number} rowVersion
 */
export function updateAccount(id, patch, rowVersion) {
  return withMockFallback(
    `PATCH /accounts/${id}`,
    async () =>
      (await apiClient.patch(`/accounts/${id}`, patch, {
        headers: { 'If-Match': `"${rowVersion}"` },
      })).data,
    () => ({ id, ...patch }),
  );
}

/**
 * Soft-delete an account. Refused with `409 account_has_children` while
 * contacts, opportunities or child accounts still point at it.
 * @param {string} id
 * @param {{reason:string, rowVersion?:number}} options
 */
export function deleteAccount(id, { reason, rowVersion } = {}) {
  return withMockFallback(
    `DELETE /accounts/${id}`,
    async () =>
      (await apiClient.delete(`/accounts/${id}`, {
        data: { reason, ...(rowVersion ? { row_version: rowVersion } : {}) },
      })).data,
    () => ({ id, deleted: true }),
  );
}

/** Undo a soft delete. @param {string} id */
export function restoreAccount(id) {
  return withMockFallback(
    `POST /accounts/${id}/restore`,
    async () => (await apiClient.post(`/accounts/${id}/restore`)).data,
    () => ({ id, deleted: false }),
  );
}

/**
 * The parent chain and/or descendant subtree, with roll-ups.
 * @param {string} id
 * @param {{direction?:'up'|'down'|'both', depth?:number, rollup?:boolean}} [params]
 */
export function getAccountHierarchy(id, params = {}) {
  return withMockFallback(
    `GET /accounts/${id}/hierarchy`,
    async () => (await apiClient.get(`/accounts/${id}/hierarchy`, { params })).data,
    () => ({ root: null, nodes: [] }),
  );
}

/**
 * The people at this company, primary first.
 * @param {string} id
 * @param {{limit?:number, cursor?:string}} [params]
 */
export function getAccountContacts(id, params = {}) {
  return withMockFallback(
    `GET /accounts/${id}/contacts`,
    async () => (await apiClient.get(`/accounts/${id}/contacts`, { params })).data,
    () => ({ data: [], page: { limit: params.limit ?? 50, next_cursor: null, prev_cursor: null, has_more: false } }),
  );
}
